/**
 * This Kuutio's run-of-play (#1174, specs/facilitation-modules.md §3): the
 * base steps read-only until the facilitator chooses to customize them, then a
 * list of their own, saved whole on every change, as the override replaces the
 * whole list. Plain strings in the Kuutio's language, the shape
 * `isValidFacilitationOverride` accepts. Reset drops back to the base.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from 'lucide-react';

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  .mark { color: rgba(134, 239, 172, 0.9); }
  .total { margin-left: auto; color: rgba(255, 255, 255, 0.5); }
`;

const BaseList = styled.ol`
  margin: 0;
  padding: 8px 10px 8px 28px;
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  line-height: 1.5;
  li { margin: 2px 0; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px auto;
  gap: 6px 8px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(134, 239, 172, 0.25);
  textarea { grid-column: 1 / -1; min-height: 48px; resize: vertical; }
  input, textarea {
    padding: 6px 8px;
    border-radius: 7px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(0, 0, 0, 0.25);
    color: #fff;
    font: inherit;
    font-size: 13px;
  }
  input[type='number'] { width: 100%; min-width: 0; }
  .tools { display: inline-flex; gap: 4px; }
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: none;
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Empty = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
`;

const clean = (list) => list.map((s) => ({
  title: (s.title || '').trim(),
  text: (s.text || '').trim(),
  minutes: Number.isInteger(s.minutes) ? s.minutes : null,
}));
const toMinutes = (v) => {
  const m = Number.parseInt(v, 10);
  return Number.isInteger(m) && m >= 0 ? m : null;
};

/**
 * @param {{ baseSteps: object[], ownSteps: object[]|null, locked?: boolean, t: Function,
 *   onChange: (steps: object[]|null) => void }} props
 *   `onChange` persists: the whole list as plain strings, or null to drop the override.
 */
export default function FacilitationStepsEditor({ baseSteps = [], ownSteps = null, locked = false, t, onChange }) {
  const customized = Array.isArray(ownSteps);
  const [list, setList] = useState(() => (customized ? clean(ownSteps) : null));
  const touched = useRef(false); // a prefilled copy of the base is not saved until something changes

  useEffect(() => {
    setList(customized ? clean(ownSteps) : null);
    touched.current = false;
  }, [customized, JSON.stringify(ownSteps)]); // eslint-disable-line react-hooks/exhaustive-deps

  const editing = Array.isArray(list);
  const total = (list || []).reduce((sum, s) => sum + (Number.isInteger(s.minutes) ? s.minutes : 0), 0);

  const commit = (next) => {
    touched.current = true;
    setList(next);
    onChange(clean(next));
  };
  const update = (i, patch) => setList((l) => l.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const blur = () => {
    if (!editing) return;
    if (!touched.current && JSON.stringify(clean(list)) === JSON.stringify(clean(customized ? ownSteps : baseSteps))) return;
    touched.current = true;
    onChange(clean(list));
  };
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };

  return (
    <div>
      <Head>
        {t('facilitationPanel.steps')}
        {customized && <span className="mark">● {t('facilitationPanel.customized')}</span>}
        {editing && total > 0 && <span className="total">{t('facilitationPanel.stepsTotal', { minutes: total })}</span>}
        {customized && !locked && (
          <Small type="button" onClick={() => { setList(null); onChange(null); }}>
            <RotateCcw size={12} />{t('facilitationPanel.reset')}
          </Small>
        )}
      </Head>
      {!editing ? (
        <>
          {baseSteps.length ? (
            <BaseList>
              {baseSteps.map((s, i) => (
                // eslint-disable-next-line react/no-array-index-key -- a run-of-play has no ids; order is identity
                <li key={i}>
                  {Number.isInteger(s.minutes) && <strong>{s.minutes} min · </strong>}
                  {s.title && <strong>{s.title}. </strong>}
                  {s.text}
                </li>
              ))}
            </BaseList>
          ) : <Empty>{t('facilitationPanel.noBaseSteps')}</Empty>}
          {!locked && (
            <Small type="button" style={{ marginTop: 6 }} onClick={() => setList(clean(baseSteps))}>
              {t('facilitationPanel.customizeSteps')}
            </Small>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((s, i) => (
            // eslint-disable-next-line react/no-array-index-key -- a run-of-play has no ids; order is identity
            <Row key={i}>
              <input aria-label={t('facilitationPanel.stepTitle')} placeholder={t('facilitationPanel.stepTitle')} disabled={locked}
                value={s.title} onChange={(e) => update(i, { title: e.target.value })} onBlur={blur} />
              <input type="number" min="0" aria-label={t('facilitationPanel.stepMinutes')} placeholder={t('facilitationPanel.stepMinutes')} disabled={locked}
                value={s.minutes ?? ''} onChange={(e) => update(i, { minutes: toMinutes(e.target.value) })} onBlur={blur} />
              <span className="tools">
                <Small type="button" title={t('facilitationPanel.moveUp')} disabled={locked || i === 0} onClick={() => move(i, -1)}><ArrowUp size={14} /></Small>
                <Small type="button" title={t('facilitationPanel.moveDown')} disabled={locked || i === list.length - 1} onClick={() => move(i, 1)}><ArrowDown size={14} /></Small>
                <Small type="button" title={t('facilitationPanel.removeStep')} disabled={locked} onClick={() => commit(list.filter((_, j) => j !== i))}><Trash2 size={14} /></Small>
              </span>
              <textarea aria-label={t('facilitationPanel.stepText')} placeholder={t('facilitationPanel.stepText')} disabled={locked}
                value={s.text} onChange={(e) => update(i, { text: e.target.value })} onBlur={blur} />
            </Row>
          ))}
          {!locked && (
            <span>
              <Small type="button" onClick={() => commit([...list, { title: '', text: '', minutes: null }])}>
                <Plus size={14} />{t('facilitationPanel.addStep')}
              </Small>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
